/**
 * AllToursCatalog — shared list of every TourOverlay tour with per-row
 * "Watch" buttons, a "Reset all tours" footer action, and rights-aware
 * disable states. Used by both `AllToursModal` and the standalone
 * `/tours` page so the experience is identical regardless of entry
 * point.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, ArrowsClockwise, CheckCircle, Lock, Sparkle } from "@phosphor-icons/react";
import {
  TOURS,
  resetAllTours,
  hasSeenTour,
  userCanSeeTour,
  armAutoStart,
  fetchTourProgress,
} from "@/lib/tours";

const TONE_CLASSES = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber:   "border-amber-200 bg-amber-50 text-amber-700",
  indigo:  "border-indigo-200 bg-indigo-50 text-indigo-700",
  violet:  "border-violet-200 bg-violet-50 text-violet-700",
};

export default function AllToursCatalog({ user, onAfterAction, compact = false }) {
  const navigate = useNavigate();
  const [serverSeen, setServerSeen] = useState([]);

  // iter146d — pull cross-device progress so the SEEN badge stays
  // accurate even if the user finished a tour from another machine.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchTourProgress();
      if (!cancelled && data?.seen) setServerSeen(data.seen);
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  const watch = (tour) => {
    armAutoStart(tour.key);
    toast.info(`Starting "${tour.title}" tour…`);
    if (typeof onAfterAction === "function") onAfterAction("watch", tour);
    navigate(tour.route);
  };

  const handleReset = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Reset all interactive page tours? The 'first time here?' banners will re-appear next time you visit those admin pages.")) return;
    const cleared = resetAllTours();
    toast.success(
      cleared > 0
        ? `Reset ${cleared} tour${cleared === 1 ? "" : "s"} — banners will re-appear on next visit.`
        : "All tours were already reset."
    );
    if (typeof onAfterAction === "function") onAfterAction("reset");
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="all-tours-catalog">
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white">
        {TOURS.map((tour) => {
          const allowed = userCanSeeTour(user, tour);
          const seen = hasSeenTour(tour.key) || (serverSeen || []).includes(tour.key);
          const tone = TONE_CLASSES[tour.tone] || TONE_CLASSES.emerald;
          return (
            <li key={tour.key}
                data-testid={`all-tours-row-${tour.key}`}
                className="flex items-start gap-3 p-3">
              <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-sm border ${tone}`}>
                <Sparkle size={13} weight="fill" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-800">{tour.title}</h3>
                  {seen ? (
                    <span data-testid={`all-tours-seen-${tour.key}`}
                          className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle size={10} weight="fill" /> Seen
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200">
                      New
                    </span>
                  )}
                  {!allowed && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200">
                      <Lock size={10} weight="bold" /> No access
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{tour.description}</p>
                {tour.routeNote && (
                  <p className="text-[11px] text-slate-500 italic mt-1">{tour.routeNote}</p>
                )}
              </div>
              <button
                data-testid={`all-tours-watch-${tour.key}`}
                disabled={!allowed}
                onClick={() => watch(tour)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm ${
                  allowed
                    ? "bg-blue-700 text-white hover:bg-blue-800"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
              >
                <Play size={11} weight="fill" /> Watch
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
        <span className="text-[11px] text-slate-500">
          Tours stay dismissed until you reset. Each tour is a 30-60 second walkthrough.
        </span>
        <button
          data-testid="all-tours-reset"
          onClick={handleReset}
          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
        >
          <ArrowsClockwise size={11} weight="bold" /> Reset all
        </button>
      </div>
    </div>
  );
}
